import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowUpTrayIcon,
  CameraIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  SparklesIcon,
  TrashIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../services/apiService";
import { ExpenseExtractSuggestions, ExpenseKind, ProfessionalExpense } from "../types";
import { toast } from 'sonner';

type QueueStatus = "pending" | "processing" | "done" | "error" | "saved";

interface ExpenseFormData {
  proveedor: string;
  cifProveedor: string;
  numeroFactura: string;
  fecha: string;
  categoria: string;
  descripcion: string;
  baseImponible: string;
  ivaPorcentaje: string;
  total: string;
  formaPago: string;
}

interface QueueItem {
  id: string;
  file: File;
  status: QueueStatus;
  suggestions?: ExpenseExtractSuggestions;
  ocrJson?: Record<string, unknown> | null;
  form: ExpenseFormData;
  error?: string;
  savedExpense?: ProfessionalExpense;
}

interface ImportExpensesDropzoneProps {
  expenseKind: ExpenseKind;
  onSaved?: (expenses: ProfessionalExpense[]) => void;
}

const ANALYZE_CONCURRENCY = 4;
const SAVE_CONCURRENCY = 3;

const CATEGORIES = [
  "Transporte",
  "Gasolina",
  "Dietas",
  "Hotel",
  "Material",
  "Software",
  "Seguros",
  "Formacion",
  "Telefonia",
  "Suministros",
  "Otros",
];

const ACCEPT_ATTR = "image/*,application/pdf,.pdf,.jpg,.jpeg,.png,.webp";

function defaultForm(): ExpenseFormData {
  return {
    proveedor: "",
    cifProveedor: "",
    numeroFactura: "",
    fecha: new Date().toISOString().split("T")[0],
    categoria: "Otros",
    descripcion: "",
    baseImponible: "",
    ivaPorcentaje: "21",
    total: "",
    formaPago: "",
  };
}

function suggestionsToForm(s: ExpenseExtractSuggestions, prev: ExpenseFormData): ExpenseFormData {
  return {
    proveedor: s.proveedor || prev.proveedor,
    cifProveedor: s.cifProveedor || prev.cifProveedor,
    numeroFactura: s.numeroFactura || prev.numeroFactura,
    fecha: s.fecha || prev.fecha,
    categoria: s.categoria && CATEGORIES.includes(s.categoria) ? s.categoria : prev.categoria,
    descripcion: s.descripcion || prev.descripcion,
    baseImponible: s.baseImponible != null ? String(s.baseImponible) : prev.baseImponible,
    ivaPorcentaje: s.ivaPorcentaje != null ? String(s.ivaPorcentaje) : prev.ivaPorcentaje,
    total: s.total != null ? String(s.total) : prev.total,
    formaPago: s.formaPago || prev.formaPago,
  };
}

function isSavableWithoutReview(item: QueueItem): boolean {
  if (item.status !== "done") return false;
  if (item.suggestions?.revisionRequerida) return false;
  if ((item.suggestions?.ocrConfidence ?? 0) < 70) return false;
  if (!item.form.proveedor.trim() || !item.form.fecha) return false;
  return true;
}

async function runWithConcurrency<T>(
  items: T[],
  concurrency: number,
  worker: (item: T) => Promise<void>
) {
  const queue = [...items];
  const runners = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
    while (queue.length) {
      const item = queue.shift();
      if (item) await worker(item);
    }
  });
  await Promise.all(runners);
}

function isAcceptedExpenseFile(file: File) {
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  if (["pdf", "jpg", "jpeg", "png", "webp"].includes(ext)) return true;
  if (file.type === "application/pdf" || file.type.startsWith("image/")) return true;
  return false;
}

const formatCurrency = (n: number) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);

export const ImportExpensesDropzone: React.FC<ImportExpensesDropzoneProps> = ({
  expenseKind,
  onSaved,
}) => {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);
  const photoRef = useRef<HTMLInputElement>(null);
  const queueRef = useRef(queue);
  const processingRef = useRef(false);
  const autoSaveRanRef = useRef(false);

  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  const updateItem = useCallback((id: string, patch: Partial<QueueItem>) => {
    setQueue((prev) => {
      const next = prev.map((item) => (item.id === id ? { ...item, ...patch } : item));
      queueRef.current = next;
      return next;
    });
  }, []);

  const analyzeOne = useCallback(
    async (item: QueueItem) => {
      updateItem(item.id, { status: "processing", error: undefined });
      try {
        const res = await apiService.extractExpenseTicket(item.file, expenseKind);
        if (res.data?.suggestions) {
          const suggestions = res.data.suggestions;
          updateItem(item.id, {
            status: "done",
            suggestions,
            ocrJson: res.data.ocrJson || null,
            form: suggestionsToForm(suggestions, item.form),
          });
        } else {
          updateItem(item.id, {
            status: "error",
            error: res.message || res.error || "No se pudo extraer datos del ticket",
            suggestions: {
              revisionRequerida: true,
              ocrConfidence: 0,
              message: res.message || res.error,
            },
          });
        }
      } catch (err) {
        updateItem(item.id, {
          status: "error",
          error: err instanceof Error ? err.message : "Error de análisis",
        });
      }
    },
    [updateItem]
  );

  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    setIsBusy(true);
    try {
      while (true) {
        const pending = queueRef.current.filter((q) => q.status === "pending");
        if (!pending.length) break;
        await runWithConcurrency(pending, ANALYZE_CONCURRENCY, analyzeOne);
      }
    } finally {
      processingRef.current = false;
      setIsBusy(false);
    }
  }, [analyzeOne]);

  const scheduleProcessing = useCallback(() => {
    queueMicrotask(() => void drainQueue());
  }, [drainQueue]);

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const accepted = Array.from(files).filter(isAcceptedExpenseFile);
      if (!accepted.length) {
        toast.error("Formato no soportado. Use PDF o imágenes (JPG, PNG, WebP).");
        return;
      }
      const tooBig = accepted.filter((f) => f.size > 8 * 1024 * 1024);
      if (tooBig.length) {
        toast.error(`Máximo 8 MB por archivo: ${tooBig.map((f) => f.name).join(", ")}`);
      }
      const valid = accepted.filter((f) => f.size <= 8 * 1024 * 1024);
      const newItems: QueueItem[] = valid.map((file) => ({
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
        file,
        status: "pending",
        form: defaultForm(),
      }));
      setQueue((prev) => {
        const next = [...prev, ...newItems];
        queueRef.current = next;
        return next;
      });
      autoSaveRanRef.current = false;
      scheduleProcessing();
    },
    [scheduleProcessing]
  );

  const saveOne = useCallback(
    async (item: QueueItem) => {
      if (!item.form.proveedor.trim() || !item.form.fecha) {
        updateItem(item.id, { status: "error", error: "Proveedor y fecha son obligatorios" });
        return;
      }
      const fd = new FormData();
      fd.append("expenseKind", expenseKind);
      fd.append("proveedor", item.form.proveedor);
      fd.append("fecha", item.form.fecha);
      fd.append("categoria", item.form.categoria);
      if (item.form.cifProveedor) fd.append("cifProveedor", item.form.cifProveedor);
      if (item.form.numeroFactura) fd.append("numeroFactura", item.form.numeroFactura);
      if (item.form.descripcion) fd.append("descripcion", item.form.descripcion);
      if (item.form.baseImponible) fd.append("baseImponible", item.form.baseImponible);
      if (item.form.ivaPorcentaje) fd.append("ivaPorcentaje", item.form.ivaPorcentaje);
      if (item.form.total) fd.append("total", item.form.total);
      if (item.form.formaPago) fd.append("formaPago", item.form.formaPago);
      if (item.suggestions?.ocrConfidence != null) {
        fd.append("ocrConfidence", String(item.suggestions.ocrConfidence));
      }
      if (item.ocrJson) fd.append("ocrJson", JSON.stringify(item.ocrJson));
      fd.append("file", item.file);

      const res = await apiService.createProfessionalExpense(fd);
      if (res.error || !res.data?.expense) {
        updateItem(item.id, {
          status: "error",
          error: res.message || res.error || "Error al guardar",
        });
        return;
      }
      updateItem(item.id, { status: "saved", savedExpense: res.data.expense, error: undefined });
    },
    [expenseKind, updateItem]
  );

  const saveAll = useCallback(
    async (targets?: QueueItem[]) => {
      const ready =
        targets || queueRef.current.filter((q) => q.status === "done" && q.form.proveedor.trim());
      if (!ready.length) return;

      setIsBusy(true);
      await runWithConcurrency(ready, SAVE_CONCURRENCY, saveOne);
      setIsBusy(false);

      const saved = queueRef.current
        .filter((q) => q.status === "saved" && q.savedExpense)
        .map((q) => q.savedExpense!);
      if (saved.length && onSaved) onSaved(saved);
    },
    [onSaved, saveOne]
  );

  useEffect(() => {
    if (!autoSaveEnabled || isBusy) {
      autoSaveRanRef.current = false;
      return;
    }
    const stillAnalyzing = queue.some((q) => q.status === "pending" || q.status === "processing");
    if (stillAnalyzing) return;
    const autoTargets = queue.filter(isSavableWithoutReview);
    if (!autoTargets.length || autoSaveRanRef.current) return;
    autoSaveRanRef.current = true;
    void saveAll(autoTargets);
  }, [autoSaveEnabled, isBusy, queue, saveAll]);

  const retryErrors = useCallback(() => {
    setQueue((prev) => {
      const next = prev.map((item) =>
        item.status === "error" && !item.form.proveedor.trim()
          ? { ...item, status: "pending" as const, error: undefined }
          : item.status === "error"
            ? { ...item, status: "done" as const, error: undefined }
            : item
      );
      queueRef.current = next;
      return next;
    });
    autoSaveRanRef.current = false;
    scheduleProcessing();
  }, [scheduleProcessing]);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const stats = useMemo(() => {
    const total = queue.length;
    const processing = queue.filter((q) => q.status === "processing").length;
    const pending = queue.filter((q) => q.status === "pending").length;
    const finished = queue.filter((q) => ["done", "error", "saved"].includes(q.status)).length;
    const readyCount = queue.filter((q) => q.status === "done").length;
    const needsReviewCount = queue.filter(
      (q) => q.status === "done" && !!q.suggestions?.revisionRequerida
    ).length;
    const savedCount = queue.filter((q) => q.status === "saved").length;
    const errorCount = queue.filter((q) => q.status === "error").length;
    return { total, processing, pending, finished, readyCount, needsReviewCount, savedCount, errorCount };
  }, [queue]);

  const allAnalysisDone = stats.total > 0 && stats.pending === 0 && stats.processing === 0;

  const visibleQueue = useMemo(() => {
    if (!autoSaveEnabled) return queue;
    return queue.filter((item) => {
      if (item.status === "pending" || item.status === "processing") return true;
      if (item.status === "error") return true;
      if (item.status === "done" && (item.suggestions?.revisionRequerida || expandedIds.has(item.id))) {
        return true;
      }
      return false;
    });
  }, [autoSaveEnabled, expandedIds, queue]);

  const hiddenSavedCount = autoSaveEnabled ? queue.filter((q) => q.status === "saved").length : 0;

  const updateForm = (id: string, patch: Partial<ExpenseFormData>) => {
    setQueue((prev) =>
      prev.map((item) => (item.id === id ? { ...item, form: { ...item.form, ...patch } } : item))
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => photoRef.current?.click()}
          className="inline-flex items-center gap-2 rounded-xl border border-emerald-300 dark:border-emerald-700 px-4 py-2 text-sm text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
        >
          <CameraIcon className="h-5 w-5" />
          Hacer foto
        </button>
        <input
          ref={photoRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => e.target.files && addFiles(e.target.files)}
        />
      </div>

      <div
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragActive(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragActive(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragActive(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragActive(false);
          if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className={`relative cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-all ${
          dragActive
            ? "border-emerald-500 bg-emerald-50/80 dark:bg-emerald-950/30 scale-[1.01]"
            : "border-zinc-300 dark:border-zinc-700 bg-white/60 dark:bg-zinc-900/40 hover:border-emerald-400 dark:hover:border-emerald-600"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTR}
          multiple
          className="hidden"
          onChange={(e) => e.target.files && addFiles(e.target.files)}
        />
        <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-emerald-500 mb-4" />
        <h3 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
          Arrastra tickets y facturas recibidas
        </h3>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 max-w-md mx-auto">
          Suelta varios PDF o imágenes: la IA los analiza en paralelo. Revisa si hace falta y guarda
          todos de una vez.
        </p>
        <p className="mt-3 text-xs text-zinc-400">PDF · JPG/PNG/WebP · Máx. 8 MB por archivo</p>
      </div>

      {queue.length > 0 && (
        <div className="space-y-3">
          {(isBusy || stats.processing > 0 || stats.pending > 0) && (
            <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/70 dark:bg-emerald-950/30 p-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 text-sm font-medium text-emerald-800 dark:text-emerald-200">
                  <SparklesIcon className="h-5 w-5 animate-pulse" />
                  {stats.processing > 0
                    ? `${stats.finished + stats.processing}/${stats.total} analizando…`
                    : `${stats.finished}/${stats.total} procesados…`}
                </div>
                <span className="text-xs text-emerald-600 dark:text-emerald-300">
                  {stats.processing} en curso · {stats.pending} en cola
                </span>
              </div>
              <div className="h-2 bg-emerald-200/60 dark:bg-emerald-900/50 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-600 transition-all duration-300"
                  style={{
                    width: `${stats.total ? Math.round((stats.finished / stats.total) * 100) : 0}%`,
                  }}
                />
              </div>
            </div>
          )}

          {allAnalysisDone && (
            <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-950/20 p-3 text-sm text-emerald-800 dark:text-emerald-200">
              Análisis completado: {stats.readyCount} listos para guardar
              {stats.needsReviewCount > 0 && ` · ${stats.needsReviewCount} requieren revisión`}
              {stats.errorCount > 0 && ` · ${stats.errorCount} con error`}
              {stats.savedCount > 0 && ` · ${stats.savedCount} guardados`}
            </div>
          )}

          {hiddenSavedCount > 0 && (
            <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300">
              {hiddenSavedCount} gasto(s) guardados automáticamente con alta confianza
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 rounded-xl border border-zinc-300 dark:border-zinc-600 px-4 py-2.5 text-sm text-zinc-700 dark:text-zinc-300 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800">
              <input
                type="checkbox"
                checked={autoSaveEnabled}
                onChange={(e) => {
                  autoSaveRanRef.current = false;
                  setAutoSaveEnabled(e.target.checked);
                }}
                className="rounded border-zinc-400 text-emerald-600 focus:ring-emerald-500"
              />
              Guardado automático
            </label>

            {stats.readyCount > 0 && (
              <button
                type="button"
                disabled={isBusy}
                onClick={() => saveAll()}
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                <CheckCircleIcon className="h-5 w-5" />
                Guardar todas ({stats.readyCount})
              </button>
            )}

            {queue.some((q) => q.status === "error") && (
              <button
                type="button"
                disabled={isBusy}
                onClick={retryErrors}
                className="inline-flex items-center gap-2 rounded-xl border border-amber-300 dark:border-amber-700 px-4 py-2.5 text-sm text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30 disabled:opacity-50"
              >
                <ArrowPathIcon className="h-5 w-5" />
                Reintentar errores
              </button>
            )}

            {queue.some((q) => q.status === "saved" || q.status === "error") && (
              <button
                type="button"
                onClick={() =>
                  setQueue((prev) => prev.filter((q) => q.status !== "saved" && q.status !== "error"))
                }
                className="rounded-xl border border-zinc-300 dark:border-zinc-600 px-4 py-2.5 text-sm text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800"
              >
                Limpiar finalizados
              </button>
            )}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {visibleQueue.map((item) => (
          <ExpenseQueueCard
            key={item.id}
            item={item}
            expanded={expandedIds.has(item.id)}
            onToggleExpand={() => toggleExpanded(item.id)}
            onRemove={() => setQueue((prev) => prev.filter((q) => q.id !== item.id))}
            onFormChange={(patch) => updateForm(item.id, patch)}
          />
        ))}
      </div>
    </div>
  );
};

const ExpenseQueueCard: React.FC<{
  item: QueueItem;
  expanded: boolean;
  onToggleExpand: () => void;
  onRemove: () => void;
  onFormChange: (patch: Partial<ExpenseFormData>) => void;
}> = ({ item, expanded, onToggleExpand, onRemove, onFormChange }) => {
  const isImage = item.file.type.startsWith("image/");
  const FileIcon = isImage ? PhotoIcon : DocumentTextIcon;
  const confidence = item.suggestions?.ocrConfidence;

  const statusColors: Record<QueueStatus, string> = {
    pending: "bg-zinc-100 text-zinc-600",
    processing: "bg-amber-100 text-amber-700",
    done: "bg-emerald-100 text-emerald-700",
    error: "bg-red-100 text-red-700",
    saved: "bg-emerald-100 text-emerald-800",
  };
  const statusLabels: Record<QueueStatus, string> = {
    pending: "Pendiente",
    processing: "Analizando…",
    done: "Listo",
    error: "Error",
    saved: "Guardado",
  };

  const canExpand = item.status === "done" || item.status === "saved";

  return (
    <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900/60 shadow-sm overflow-hidden">
      <div
        className={`flex items-start justify-between gap-4 p-5 ${canExpand ? "cursor-pointer hover:bg-zinc-50/80 dark:hover:bg-zinc-800/40" : ""}`}
        onClick={() => canExpand && onToggleExpand()}
        role={canExpand ? "button" : undefined}
        tabIndex={canExpand ? 0 : undefined}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <FileIcon className={`h-8 w-8 shrink-0 ${isImage ? "text-sky-500" : "text-red-500"}`} />
          <div className="min-w-0">
            <p className="font-medium text-zinc-900 dark:text-zinc-100 truncate">{item.file.name}</p>
            <p className="text-xs text-zinc-500">
              {item.form.proveedor || "Sin proveedor"}
              {item.form.total && ` · ${formatCurrency(parseFloat(item.form.total) || 0)}`}
              {item.form.fecha && ` · ${item.form.fecha}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[item.status]}`}>
            {statusLabels[item.status]}
          </span>
          {canExpand && (
            <ChevronDownIcon
              className={`h-5 w-5 text-zinc-400 transition-transform ${expanded ? "rotate-180" : ""}`}
            />
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="p-1 text-zinc-400 hover:text-red-500"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="px-5 pb-5">
        {item.status === "processing" && (
          <div className="space-y-2 animate-pulse py-2">
            <div className="h-3 bg-zinc-200 dark:bg-zinc-700 rounded w-3/4" />
            <div className="h-3 bg-zinc-200 dark:bg-zinc-700 rounded w-1/2" />
          </div>
        )}

        {item.error && (
          <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-700 dark:text-red-300 mb-3">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0 mt-0.5" />
            <span>{item.error}</span>
          </div>
        )}

        {canExpand && expanded && (
          <div className="space-y-3 pt-1">
            <div className="flex flex-wrap gap-2">
              {confidence != null && (
                <span className="text-xs px-2 py-1 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600">
                  Confianza IA: {confidence}%
                </span>
              )}
              {item.suggestions?.revisionRequerida && (
                <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                  Necesita revisión
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field
                label="Proveedor *"
                value={item.form.proveedor}
                onChange={(v) => onFormChange({ proveedor: v })}
              />
              <Field
                label="Fecha *"
                type="date"
                value={item.form.fecha}
                onChange={(v) => onFormChange({ fecha: v })}
              />
              <Field
                label="CIF proveedor"
                value={item.form.cifProveedor}
                onChange={(v) => onFormChange({ cifProveedor: v })}
              />
              <Field
                label="Nº factura/ticket"
                value={item.form.numeroFactura}
                onChange={(v) => onFormChange({ numeroFactura: v })}
              />
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Categoría</label>
                <select
                  value={item.form.categoria}
                  onChange={(e) => onFormChange({ categoria: e.target.value })}
                  className="w-full rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-sm"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <Field
                label="Total"
                type="number"
                value={item.form.total}
                onChange={(v) => onFormChange({ total: v })}
              />
              <Field
                label="Base imponible"
                type="number"
                value={item.form.baseImponible}
                onChange={(v) => onFormChange({ baseImponible: v })}
              />
              <Field
                label="IVA %"
                type="number"
                value={item.form.ivaPorcentaje}
                onChange={(v) => onFormChange({ ivaPorcentaje: v })}
              />
              <Field
                label="Descripción"
                value={item.form.descripcion}
                onChange={(v) => onFormChange({ descripcion: v })}
              />
            </div>
          </div>
        )}

        {item.status === "saved" && (
          <p className="mt-2 text-sm text-emerald-600 flex items-center gap-1">
            <CheckCircleIcon className="h-4 w-4" />
            Gasto guardado correctamente
          </p>
        )}
      </div>
    </div>
  );
};

const Field: React.FC<{
  label: string;
  value: string;
  type?: string;
  onChange: (v: string) => void;
}> = ({ label, value, type = "text", onChange }) => (
  <div>
    <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">{label}</label>
    <input
      type={type}
      value={value}
      step={type === "number" ? "0.01" : undefined}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-sm"
    />
  </div>
);
